import React, { useEffect, useState } from "react";
import axios from "axios";
import Sidebar from "../components/Sidebar";
import WorkerProfileModal from "../components/WorkerProfileModal";
import { toast } from "react-hot-toast";
import { RefreshCw } from "lucide-react";

const API_BASE = process.env.REACT_APP_API_BASE; // ✅ unified API root

const ManageWorkers = () => {
  const [workers, setWorkers] = useState([]);
  const [filter, setFilter] = useState("all");
  const [selectedWorker, setSelectedWorker] = useState(null);
  const [loading, setLoading] = useState(false);

  // ============================================================
  // 📡 Fetch Workers
  // ============================================================
  const loadWorkers = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/admin/workers`);
      setWorkers(res.data || []);
    } catch (err) {
      console.error("❌ Failed to fetch workers:", err);
      toast.error("Failed to load workers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWorkers();
  }, []);

  // ============================================================
  // 🧾 Update Worker Status
  // ============================================================
  const updateWorkerStatus = async (workerId, status) => {
    try {
      await axios.post(`${API_BASE}/admin/update-status/${workerId}`, { status });
      setWorkers((prev) =>
        prev.map((w) => (w._id === workerId ? { ...w, status } : w))
      );
      setSelectedWorker(null);
      toast.success(`✅ Worker ${status} successfully.`);
    } catch (err) {
      console.error("Failed to update worker:", err);
      toast.error("Failed to update worker status.");
    }
  };

  // ============================================================
  // 🔍 Filter Workers
  // ============================================================
  const filteredWorkers = workers.filter((worker) =>
    filter === "all" ? true : worker.status === filter
  );

  // ============================================================
  // 🖥️ Render
  // ============================================================
  return (
    <div className="flex font-[Poppins]">
      <Sidebar />

      <main className="ml-0 md:ml-64 p-6 w-full bg-gray-100 min-h-screen">
        <div className="flex items-center justify-between mb-8">
          <h1 className="text-3xl font-extrabold text-gray-800">
            🧰 Manage Workers
          </h1>

          <div className="flex items-center gap-4">
            <label className="font-semibold text-gray-700">Filter:</label>
            <select
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              className="border border-gray-300 px-4 py-2 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="all">All</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="incomplete">Incomplete</option>
            </select>

            <button
              onClick={loadWorkers}
              disabled={loading}
              className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded-lg font-semibold disabled:opacity-70"
            >
              <RefreshCw
                className={`w-4 h-4 ${loading ? "animate-spin" : ""}`}
              />
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-x-auto bg-white rounded-2xl shadow">
          <table className="min-w-full text-sm text-left">
            <thead className="bg-gray-100 text-gray-700 uppercase text-xs tracking-wider">
              <tr>
                <th className="px-4 py-3">#</th>
                <th className="px-4 py-3">Name</th>
                <th className="px-4 py-3">Email</th>
                <th className="px-4 py-3">Phone</th>
                <th className="px-4 py-3">Status</th>
                <th className="px-4 py-3">Jobs</th>
                <th className="px-4 py-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredWorkers.map((worker, index) => (
                <tr
                  key={worker._id}
                  className="border-b hover:bg-gray-50 transition duration-150"
                >
                  <td className="px-4 py-3">{index + 1}</td>
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {worker.name || "Unnamed"}
                  </td>
                  <td className="px-4 py-3">{worker.email}</td>
                  <td className="px-4 py-3">{worker.phone || "N/A"}</td>
                  <td className="px-4 py-3 capitalize font-semibold">
                    <span
                      className={`px-2 py-1 rounded text-xs font-semibold ${
                        worker.status === "approved"
                          ? "bg-green-100 text-green-700"
                          : worker.status === "pending"
                          ? "bg-yellow-100 text-yellow-700"
                          : worker.status === "rejected"
                          ? "bg-red-100 text-red-700"
                          : "bg-gray-100 text-gray-700"
                      }`}
                    >
                      {worker.status}
                    </span>
                  </td>
                  <td className="px-4 py-3">{worker.jobsAssigned?.length || 0}</td>
                  <td className="px-4 py-3 space-x-2">
                    <button
                      onClick={() => setSelectedWorker(worker)}
                      className="bg-blue-600 text-white px-3 py-1 rounded hover:bg-blue-700 text-sm"
                    >
                      Quick View
                    </button>
                    <button
                      onClick={() =>
                        (window.location.href = `/admin/worker/${worker._id}`)
                      }
                      className="bg-gray-800 text-white px-3 py-1 rounded hover:bg-gray-900 text-sm"
                    >
                      Profile
                    </button>
                  </td>
                </tr>
              ))}
              {filteredWorkers.length === 0 && !loading && (
                <tr>
                  <td
                    colSpan="7"
                    className="px-4 py-4 text-center text-gray-500"
                  >
                    No workers found for this filter.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Worker Modal */}
        {selectedWorker && (
          <WorkerProfileModal
            worker={selectedWorker}
            onClose={() => setSelectedWorker(null)}
            onApprove={() =>
              updateWorkerStatus(selectedWorker._id, "approved")
            }
            onReject={() => updateWorkerStatus(selectedWorker._id, "rejected")}
          />
        )}
      </main>
    </div>
  );
};

export default ManageWorkers;
